import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../api/apiClient";
import { shopModeApi, type FobSummary } from "../api/shopMode";
import type {
  AppLanguage,
  Location,
  UserListEntry,
  UpdateUserContactPayload,
  UpdateUserCompensationPayload,
  UpdateUserPayTermsPayload,
} from "../api/types";
import { Modal } from "../components/Modal";
import { TeamCardList } from "../components/team/TeamCardList";
import {
  TeamFilterBar,
  type TeamRoleFilter,
} from "../components/team/TeamFilterBar";
import { PasswordResetEmailModal } from "../components/team/PasswordResetEmailModal";
import { InviteModal } from "../components/team/InviteModal";
import { TeamMemberSheet } from "../components/team/TeamMemberSheet";
import { teamCountLine } from "../components/team/teamLabels";
import { useAuth } from "../state/AuthContext";
import {
  teamMemberPermissions,
  type TeamViewer,
} from "../utils/teamMemberPermissions";
import {
  fobAntennaHint,
  fobErrorMessage,
  holdFobReader,
  releaseFobReader,
  nfcStatus,
  nfcSupported,
  writeFob,
} from "../platform/nfc";
import { parseTypedAmount } from "../utils/typedAmount";
import { businessToday } from "../utils/businessDay";
import { money } from "../i18n/intl";
import { AmountReadBack } from "../components/finances/AmountReadBack";

export function UsersPage() {
  const { user: sessionUser, hasRole } = useAuth();
  const [users, setUsers] = useState<UserListEntry[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [scheduleLocationId, setScheduleLocationId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reassign, setReassign] = useState<UserListEntry | null>(null);
  const [passwordUser, setPasswordUser] = useState<UserListEntry | null>(null);
  const [inviteUser, setInviteUser] = useState<UserListEntry | null>(null);
  const [payUser, setPayUser] = useState<UserListEntry | null>(null);
  /** G-163 (D-78): deactivation asks the last working day (required for a salaried person). */
  const [deactivateUser, setDeactivateUser] = useState<UserListEntry | null>(null);
  const [contactUser, setContactUser] = useState<UserListEntry | null>(null);
  const [contractUser, setContractUser] = useState<UserListEntry | null>(null);
  const [pinUser, setPinUser] = useState<UserListEntry | null>(null);
  const [roleFilter, setRoleFilter] = useState<TeamRoleFilter>("");
  const [includeInactive, setIncludeInactive] = useState(false);
  /*
   * G-102 — the id, NOT the row. The language select patches one row in place
   * rather than refetching (see `setLanguage` below), so a sheet holding a
   * captured copy would keep showing the language the person just changed.
   */
  const [sheetUserId, setSheetUserId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [u, l] = await Promise.all([
        api.listUsers(),
        api.listLocations(),
      ]);
      setUsers(u);
      setLocations(l.filter((loc) => loc.isActive));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not load users");
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * G-85 S6 — set a colleague's language from Manage team (owner, 2026-09-16:
   * "manager owner can set up accounts default language in the 'manage team'").
   * "" clears it, which is not the same as choosing English: with no
   * preference the kiosk follows whatever the tablet is showing.
   */
  const setLanguage = useCallback(
    async (target: UserListEntry, value: string) => {
      setError(null);
      try {
        const updated = await api.patchUserLanguage(
          target.id,
          value === "" ? null : (value as AppLanguage),
        );
        // Patch the ONE row from the response rather than calling refresh():
        // refresh() sets `loading`, which replaces every row with a single
        // "Loading…" cell — destroying the <select> the person is still
        // holding, losing its focus and the scroll position. This is the only
        // inline row-mutating control on the page, so it is the only place
        // that shows (G-85 S6 review). The endpoint already returns the
        // updated row, so refetching users AND locations bought nothing.
        setUsers((prev) =>
          prev.map((u) => (u.id === updated.id ? updated : u)),
        );
      } catch (err: unknown) {
        setError(
          err instanceof Error ? err.message : "Could not set the language",
        );
      }
    },
    [],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (locations.length === 0) {
      setScheduleLocationId("");
      return;
    }
    setScheduleLocationId((prev) =>
      prev && locations.some((l) => l.id === prev) ? prev : locations[0].id,
    );
  }, [locations]);

  const sortedUsers = useMemo(
    () =>
      [...users].sort((a, b) => {
        if (a.role !== b.role) return a.role.localeCompare(b.role);
        return a.fullName.localeCompare(b.fullName);
      }),
    [users],
  );

  const viewer = useMemo<TeamViewer>(
    () => ({ id: sessionUser?.id, role: sessionUser?.role ?? null }),
    [sessionUser?.id, sessionUser?.role],
  );

  /** What the chips and the toggle leave on screen — one list, both widths. */
  const visibleUsers = useMemo(
    () =>
      sortedUsers.filter(
        (u) =>
          (includeInactive || u.isActive) && (roleFilter === "" || u.role === roleFilter),
      ),
    [sortedUsers, includeInactive, roleFilter],
  );

  /** Counts respect the inactive toggle, so a chip never promises hidden rows. */
  const roleCounts = useMemo(() => {
    const pool = sortedUsers.filter((u) => includeInactive || u.isActive);
    const counts: Record<TeamRoleFilter, number> = {
      "": pool.length,
      OWNER: 0,
      ADMIN: 0,
      MANAGER: 0,
      FLORIST: 0,
      COURIER: 0,
      HOUSEKEEPING: 0,
    };
    for (const u of pool) counts[u.role] += 1;
    return counts;
  }, [sortedUsers, includeInactive]);

  const inactiveCount = useMemo(
    () => sortedUsers.filter((u) => !u.isActive).length,
    [sortedUsers],
  );

  /*
   * "Nobody matches this filter" and "No users yet" are different facts, and
   * the inactive toggle only counts as a filter when it is actually hiding
   * somebody. Without that second clause the toggle's `false` default made
   * every empty list read as a filter miss — including a brand-new shop with
   * no team at all, told to try another role (found by /code-review).
   */
  const isFiltered = roleFilter !== "" || (!includeInactive && inactiveCount > 0);

  /*
   * Whether one of the sheet's modals is covering it — see `behindModal` in
   * `TeamMemberSheet`. Every modal this page can open is listed, because the
   * one left out is the one that lets Tab reach the sheet underneath it.
   */
  const modalOpen = Boolean(
    pinUser ||
      contactUser ||
      contractUser ||
      payUser ||
      reassign ||
      passwordUser ||
      inviteUser ||
      deactivateUser,
  );

  // Derived, never stored: see `sheetUserId`.
  const sheetUser = useMemo(
    () => (sheetUserId ? (users.find((u) => u.id === sheetUserId) ?? null) : null),
    [users, sheetUserId],
  );

  /*
   * G-106 — the sheet STAYS OPEN and the modal opens on top of it.
   *
   * G-102 closed it first, on the reasoning that returning to a finished sheet
   * is clutter. On the shop tablet it reads the other way round: every action
   * dropped you back to the card list, so setting a PIN and then fixing a
   * phone number meant finding the same person twice. Closing the modal now
   * returns you to the person you were working on.
   *
   * Three things make that safe, and none is incidental:
   *  - `sheetUser` is DERIVED from `sheetUserId`, so each modal's `onDone →
   *    refresh()` flows into the sheet instead of leaving it holding a stale
   *    row (a captured row would still say "No PIN" after one was set);
   *  - `useDismissOnEscape` closes the top overlay only, so one Escape leaves
   *    the sheet standing;
   *  - `.team-sheet-backdrop` drops to `z-index: 290`, so the modal paints
   *    above the sheet without depending on the JSX order below.
   *
   * The sheet's own actions re-derive from the refreshed row too: deactivating
   * somebody leaves the sheet open on a person who is now inactive, with the
   * Deactivate row correctly gone.
   */

  /*
   * G-145 — `?open=<id>` opens that person's panel once the list has loaded
   * (Create user lands here with the new person open). The parameter is then
   * dropped so a reload or Back does not reopen it.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const openParam = searchParams.get("open");
  useEffect(() => {
    if (!openParam || loading) return;
    if (users.some((u) => u.id === openParam)) setSheetUserId(openParam);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("open");
        return next;
      },
      { replace: true },
    );
  }, [openParam, loading, users, setSearchParams]);

  /** G-145 — the panel's "View": the stored PDF in a new tab, as the Contract dialog does. */
  const viewContract = useCallback(async (target: UserListEntry) => {
    setError(null);
    try {
      const url = await api.fetchUserWorkContractBlobUrl(target.id);
      if (!url) {
        setError("Could not open the contract PDF.");
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
      // The new tab has its own copy once loaded; free ours after a while.
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not open the contract PDF.");
    }
  }, []);


  return (
    <section className="team-page">
      <div className="team-page-header">
        <div>
          <h2 className="page-title">Team</h2>
          <p className="page-subtitle">{loading && users.length === 0 ? "\u00a0" : teamCountLine(users)}</p>
        </div>
        <div className="team-page-actions">
          <label htmlFor="team-schedule-location" className="team-page-schedule-label">
            Schedule
          </label>
          <select
            id="team-schedule-location"
            className="team-page-select"
            value={scheduleLocationId}
            onChange={(e) => setScheduleLocationId(e.target.value)}
            disabled={locations.length === 0}
          >
            {locations.length === 0 ? (
              <option value="">No shops yet</option>
            ) : (
              locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))
            )}
          </select>
          {scheduleLocationId ? (
            <Link
              to={`/app/schedule?locationId=${encodeURIComponent(scheduleLocationId)}`}
              className="button tinted"
            >
              Open schedule
            </Link>
          ) : (
            <button type="button" className="button tinted" disabled>
              Open schedule
            </button>
          )}
          {hasRole("OWNER") ? (
            <Link to="/app/users/new" className="button">
              New user
            </Link>
          ) : null}
        </div>
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      <TeamFilterBar
        role={roleFilter}
        onRoleChange={setRoleFilter}
        includeInactive={includeInactive}
        onIncludeInactiveChange={setIncludeInactive}
        counts={roleCounts}
        inactiveCount={inactiveCount}
      />

      {/* G-145 — tiles at every width; the 15-column table that scrolled
          sideways on a computer is gone. */}
      <TeamCardList
        users={visibleUsers}
        viewer={viewer}
        loading={loading}
        filtered={isFiltered}
        onOpen={(u) => setSheetUserId(u.id)}
      />

      {sheetUser ? (
        <TeamMemberSheet
          user={sheetUser}
          permissions={teamMemberPermissions(sheetUser, viewer)}
          onEditContact={() => setContactUser(sheetUser)}
          onEditContract={() => setContractUser(sheetUser)}
          onViewContract={() => void viewContract(sheetUser)}
          onResetPassword={() => setPasswordUser(sheetUser)}
          onManageInvite={() => setInviteUser(sheetUser)}
          onEditPay={() => setPayUser(sheetUser)}
          onSetPin={() => setPinUser(sheetUser)}
          onReassign={() => setReassign(sheetUser)}
          onDeactivate={() => setDeactivateUser(sheetUser)}
          onSetLanguage={(value) => void setLanguage(sheetUser, value)}
          behindModal={modalOpen}
          onClose={() => setSheetUserId(null)}
        />
      ) : null}

      {pinUser ? (
        <ShopPinModal
          user={pinUser}
          onClose={() => setPinUser(null)}
          // Refresh the "PIN configured" badge WITHOUT closing — the modal must
          // stay open so the just-generated PIN can actually be read out.
          onDone={() => void refresh()}
        />
      ) : null}

      {contactUser ? (
        <ContactModal
          user={contactUser}
          canChangeEmail={teamMemberPermissions(contactUser, viewer).canChangeEmail}
          onClose={() => setContactUser(null)}
          onDone={async () => {
            setContactUser(null);
            await refresh();
          }}
        />
      ) : null}

      {contractUser ? (
        <ContractModal
          user={contractUser}
          onClose={() => setContractUser(null)}
          onDone={async () => {
            setContractUser(null);
            await refresh();
          }}
        />
      ) : null}

      {deactivateUser ? (
        <DeactivateModal
          user={deactivateUser}
          onClose={() => setDeactivateUser(null)}
          onDone={async () => {
            setDeactivateUser(null);
            await refresh();
          }}
        />
      ) : null}

      {payUser ? (
        <PayCompensationModal
          user={payUser}
          onClose={() => setPayUser(null)}
          onDone={async () => {
            setPayUser(null);
            await refresh();
          }}
        />
      ) : null}

      {reassign ? (
        <ReassignModal
          user={reassign}
          locations={locations}
          onClose={() => setReassign(null)}
          onDone={async () => {
            setReassign(null);
            await refresh();
          }}
        />
      ) : null}

      {passwordUser ? (
        <PasswordResetEmailModal
          user={passwordUser}
          onClose={() => setPasswordUser(null)}
        />
      ) : null}

      {inviteUser ? (
        <InviteModal
          user={inviteUser}
          onClose={() => setInviteUser(null)}
          // Patch the one row, like `setLanguage` — the dialog stays open on
          // its confirmation, and a full refresh would blank the list under it.
          onChanged={(updated) => {
            setInviteUser(updated);
            setUsers((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
          }}
        />
      ) : null}

    </section>
  );
}

/**
 * G-163 (owner 2026-10-19, D-78): replaces the native confirm. A salaried
 * person's last working day is asked (today by default, never later): their
 * last month's salary is paid pro rata to it. Hourly people are paid by their
 * shifts, so they are not asked.
 */
function DeactivateModal({
  user,
  onClose,
  onDone,
}: {
  user: UserListEntry;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const today = businessToday().ymd;
  // Salaried now, or a salary already scheduled: the server needs the day.
  const salaried = user.salariedNowOrLater === true;
  const [lastWorkingDay, setLastWorkingDay] = useState(today);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const submit = async () => {
    setLocalError(null);
    if (salaried && (!/^\d{4}-\d{2}-\d{2}$/.test(lastWorkingDay) || lastWorkingDay > today)) {
      setLocalError("Last working day: today or earlier.");
      return;
    }
    setSubmitting(true);
    try {
      await api.deleteUser(user.id, salaried ? lastWorkingDay : undefined);
      await onDone();
    } catch (err: unknown) {
      setLocalError(err instanceof Error ? err.message : "Could not deactivate user");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title={`Deactivate · ${user.fullName}`} onClose={onClose}>
      <div className="team-deactivate">
        <p>No sign-in · shop assignments end</p>
        {localError ? <div className="alert error">{localError}</div> : null}
        {salaried ? (
          <div className="field">
            <label htmlFor="deactivate-last-day">Last working day</label>
            <input
              id="deactivate-last-day"
              type="date"
              max={today}
              value={lastWorkingDay}
              onChange={(e) => setLastWorkingDay(e.target.value)}
            />
          </div>
        ) : null}
        <div className="button-row">
          <button type="button" className="button plain" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="button danger" onClick={() => void submit()} disabled={submitting}>
            {submitting ? "Deactivating…" : "Deactivate"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function PayCompensationModal({
  user,
  onClose,
  onDone,
}: {
  user: UserListEntry;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [hourlyStr, setHourlyStr] = useState(user.hourlyRateEur ?? "");
  const [commissionStr, setCommissionStr] = useState(user.salesCommissionPercent ?? "");
  const [clearHourly, setClearHourly] = useState(false);
  const [clearCommission, setClearCommission] = useState(false);
  // G-103 S2 (OD-82/OD-84): salary and pay day live on the pay-terms row.
  const [salaryStr, setSalaryStr] = useState(user.monthlySalaryEur ?? "");
  const [clearSalary, setClearSalary] = useState(false);
  const [payDayStr, setPayDayStr] = useState(String(user.payDayOfMonth ?? 1));
  // G-163 (owner 2026-10-19): a salary change starts this month or a later one.
  const today = businessToday();
  const thisMonthKey = `${today.year}-${String(today.month).padStart(2, "0")}`;
  const [salaryStartsFrom, setSalaryStartsFrom] = useState(thisMonthKey);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const submit = async () => {
    setLocalError(null);
    const terms: UpdateUserPayTermsPayload = {};
    if (clearSalary) {
      terms.monthlySalaryEur = null;
    } else if (salaryStr.trim() !== "" && salaryStr.trim() !== (user.monthlySalaryEur ?? "")) {
      const read = parseTypedAmount(salaryStr);
      if (!read.ok || read.value < 0) {
        setLocalError(read.ok ? "Monthly salary must be a non-negative number." : `Monthly salary: ${read.message}`);
        return;
      }
      terms.monthlySalaryEur = read.value;
    }
    const payDay = Number(payDayStr.trim());
    if (!Number.isInteger(payDay) || payDay < 1 || payDay > 31) {
      setLocalError("Pay day must be a day of the month, 1–31.");
      return;
    }
    if (payDay !== (user.payDayOfMonth ?? 1)) terms.payDayOfMonth = payDay;
    if (terms.monthlySalaryEur !== undefined && salaryStartsFrom !== thisMonthKey) {
      if (!/^\d{4}-\d{2}$/.test(salaryStartsFrom) || salaryStartsFrom < thisMonthKey) {
        setLocalError("Starts from: this month or a later one.");
        return;
      }
      terms.salaryStartsFrom = salaryStartsFrom;
    }
    const payload: UpdateUserCompensationPayload = {};
    if (clearHourly) {
      payload.hourlyRateEur = null;
    } else if (hourlyStr.trim() !== "") {
      const read = parseTypedAmount(hourlyStr);
      if (!read.ok || read.value < 0) {
        setLocalError(read.ok ? "Hourly rate must be a non-negative number." : `Hourly rate: ${read.message}`);
        return;
      }
      payload.hourlyRateEur = read.value;
    }
    if (clearCommission) {
      payload.salesCommissionPercent = null;
    } else if (commissionStr.trim() !== "") {
      const read = parseTypedAmount(commissionStr);
      if (!read.ok || read.value < 0 || read.value > 100) {
        setLocalError(read.ok ? "Commission must be between 0 and 100." : `Commission: ${read.message}`);
        return;
      }
      payload.salesCommissionPercent = read.value;
    }
    if (Object.keys(payload).length === 0 && Object.keys(terms).length === 0) {
      setLocalError("Change a value, or tick a clear checkbox, then save.");
      return;
    }
    setSubmitting(true);
    try {
      // The server refuses a salary beside an hourly rate (and the reverse),
      // so whichever call CLEARS goes first: clearing the salary before
      // setting a rate, clearing the rate before setting a salary — both
      // switches are one save.
      const salaryFirst = terms.monthlySalaryEur === null;
      const calls = [
        () => (Object.keys(payload).length > 0 ? api.patchUserCompensation(user.id, payload) : null),
        () => (Object.keys(terms).length > 0 ? api.patchUserPayTerms(user.id, terms) : null),
      ];
      if (salaryFirst) calls.reverse();
      for (const run of calls) await run();
      await onDone();
    } catch (err: unknown) {
      setLocalError(err instanceof Error ? err.message : "Could not save");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title={`Pay rates · ${user.fullName}`} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        <p className="muted" style={{ margin: 0, fontSize: "0.9rem" }}>
          Hourly pay (EUR) and commission on sale totals for sales this person logs. Leave a field
          empty to leave it unchanged; tick clear to remove a stored rate.
        </p>
        {localError ? <div className="alert error">{localError}</div> : null}
        <div className="field">
          <label htmlFor="pay-hourly">Hourly rate (€)</label>
          <input
            id="pay-hourly"
            type="text"
            inputMode="decimal"
            disabled={clearHourly}
            value={hourlyStr}
            onChange={(e) => setHourlyStr(e.target.value)}
            placeholder="e.g. 14.50"
          />
          {clearHourly ? null : <AmountReadBack raw={hourlyStr} />}
          <label style={{ display: "flex", gap: "var(--space-2)", marginTop: "var(--space-2)", alignItems: "center" }}>
            <input
              type="checkbox"
              checked={clearHourly}
              onChange={(e) => {
                setClearHourly(e.target.checked);
                if (e.target.checked) setHourlyStr("");
              }}
            />
            <span className="hint">Clear hourly rate</span>
          </label>
        </div>
        <div className="field">
          <label htmlFor="pay-commission">Commission (% of sale total)</label>
          <input
            id="pay-commission"
            type="text"
            inputMode="decimal"
            disabled={clearCommission}
            value={commissionStr}
            onChange={(e) => setCommissionStr(e.target.value)}
            placeholder="e.g. 3"
          />
          <label style={{ display: "flex", gap: "var(--space-2)", marginTop: "var(--space-2)", alignItems: "center" }}>
            <input
              type="checkbox"
              checked={clearCommission}
              onChange={(e) => {
                setClearCommission(e.target.checked);
                if (e.target.checked) setCommissionStr("");
              }}
            />
            <span className="hint">Clear commission %</span>
          </label>
        </div>
        <div className="field">
          <label htmlFor="pay-salary">Monthly salary (€) — salaried staff only</label>
          <input
            id="pay-salary"
            type="text"
            inputMode="decimal"
            disabled={clearSalary}
            value={salaryStr}
            onChange={(e) => setSalaryStr(e.target.value)}
            placeholder="e.g. 1500"
          />
          {clearSalary ? null : <AmountReadBack raw={salaryStr} />}
          <label style={{ display: "flex", gap: "var(--space-2)", marginTop: "var(--space-2)", alignItems: "center" }}>
            <input
              type="checkbox"
              checked={clearSalary}
              onChange={(e) => {
                setClearSalary(e.target.checked);
                if (e.target.checked) setSalaryStr("");
              }}
            />
            <span className="hint">Clear salary (back to hourly)</span>
          </label>
          <label htmlFor="pay-salary-from" className="team-pay-starts-from">
            Starts from
          </label>
          <input
            id="pay-salary-from"
            type="month"
            min={thisMonthKey}
            value={salaryStartsFrom}
            onChange={(e) => setSalaryStartsFrom(e.target.value)}
          />
          {user.upcomingSalary ? (
            <small className="hint">
              From {user.upcomingSalary.fromMonth}:{" "}
              {user.upcomingSalary.monthlySalaryEur != null ? money(user.upcomingSalary.monthlySalaryEur) : "hourly"}
            </small>
          ) : null}
          <small className="hint">
            A person is salaried or hourly, never both — clear one before setting the other.
          </small>
        </div>
        <div className="field">
          <label htmlFor="pay-day">Pay day (day of month)</label>
          <input
            id="pay-day"
            type="number"
            inputMode="numeric"
            min={1}
            max={31}
            value={payDayStr}
            onChange={(e) => setPayDayStr(e.target.value)}
          />
          <small className="hint">
            When their salary is due. A day the month lacks falls on its last day.
          </small>
        </div>
        <div className="button-row">
          <button type="button" className="button plain" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="button" onClick={() => void submit()} disabled={submitting}>
            {submitting ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ContactModal({
  user,
  canChangeEmail,
  onClose,
  onDone,
}: {
  user: UserListEntry;
  canChangeEmail: boolean;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [firstName, setFirstName] = useState(user.firstName ?? "");
  const [lastName, setLastName] = useState(user.lastName ?? "");
  const [phoneNumber, setPhoneNumber] = useState(user.phoneNumber ?? "");
  const [email, setEmail] = useState(user.email);
  const [jobTitle, setJobTitle] = useState(user.jobTitle ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (!firstName.trim() || !lastName.trim()) {
      setError("Name and surname are required.");
      return;
    }
    if (!email.trim()) {
      setError("Email is required.");
      return;
    }
    const payload: UpdateUserContactPayload = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phoneNumber: phoneNumber.trim() || null,
      // Not sent when it cannot change here — the server would refuse the whole save.
      ...(canChangeEmail ? { email: email.trim().toLowerCase() } : {}),
      jobTitle: jobTitle.trim() || null,
    };
    setSubmitting(true);
    try {
      await api.patchUserContact(user.id, payload);
      await onDone();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not save contact");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title={`Contact · ${user.fullName}`} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        {error ? <div className="alert error">{error}</div> : null}
        <div className="form-grid">
          <div className="field">
            <label htmlFor="contact-first-name">Name *</label>
            <input
              id="contact-first-name"
              type="text"
              maxLength={80}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="contact-last-name">Surname *</label>
            <input
              id="contact-last-name"
              type="text"
              maxLength={80}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="contact-phone">Phone number</label>
            <input
              id="contact-phone"
              type="tel"
              maxLength={40}
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="contact-email">Email *</label>
            <input
              id="contact-email"
              type="email"
              maxLength={254}
              value={email}
              disabled={!canChangeEmail}
              onChange={(e) => setEmail(e.target.value)}
            />
            <span className="hint">
              {!canChangeEmail
                ? user.role === "OWNER" || user.role === "ADMIN"
                  ? "Owners and admins change their own sign-in email from their profile, where their password is required."
                  : "Change your own email from your profile, where your current password is required."
                : user.accountStatus === "ACTIVE"
                ? "A new address takes over only once it confirms the link we email to it. Until then they sign in with the current one, which is told about the change."
                : "They have not set up their account yet, so a new address applies at once. If their invitation is still open, a fresh one goes to the new address."}
            </span>
            {user.pendingEmail ? (
              <span className="hint">Waiting for {user.pendingEmail} to confirm.</span>
            ) : null}
          </div>
          <div className="field">
            <label htmlFor="contact-job-title">Job title</label>
            <input
              id="contact-job-title"
              type="text"
              maxLength={80}
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
            />
          </div>
        </div>
        <div className="button-row">
          <button type="button" className="button plain" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="button" onClick={() => void submit()} disabled={submitting}>
            {submitting ? "Saving…" : "Save contact"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ContractModal({
  user,
  onClose,
  onDone,
}: {
  user: UserListEntry;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const viewContract = async () => {
    setPreviewLoading(true);
    setError(null);
    try {
      const url = await api.fetchUserWorkContractBlobUrl(user.id);
      if (!url) {
        setError("Could not open contract PDF.");
        return;
      }
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
      window.open(url, "_blank", "noopener,noreferrer");
    } finally {
      setPreviewLoading(false);
    }
  };

  const submit = async () => {
    setError(null);
    if (!file) {
      setError("Choose a PDF contract first.");
      return;
    }
    if (file.type !== "application/pdf") {
      setError("Contract must be a PDF.");
      return;
    }
    setSubmitting(true);
    try {
      await api.uploadUserWorkContract(user.id, file);
      await onDone();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not upload contract");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title={`Work contract · ${user.fullName}`} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        {error ? <div className="alert error">{error}</div> : null}
        <div>
          <strong>Status:</strong>{" "}
          {user.workContractConfigured ? (
            <span className="badge success">PDF uploaded</span>
          ) : (
            <span className="muted">No contract uploaded</span>
          )}
          {user.workContractOriginalName ? (
            <p className="muted" style={{ margin: "var(--space-1) 0 0" }}>
              {user.workContractOriginalName}
            </p>
          ) : null}
        </div>
        {user.workContractConfigured ? (
          <button
            type="button"
            className="button plain"
            onClick={() => void viewContract()}
            disabled={previewLoading}
          >
            {previewLoading ? "Opening…" : "View current PDF"}
          </button>
        ) : null}
        <div className="field">
          <label htmlFor="contract-file">Upload replacement PDF</label>
          <input
            id="contract-file"
            type="file"
            accept="application/pdf,.pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          <small className="hint">PDF only, up to 10 MB.</small>
        </div>
        <div className="button-row">
          <button type="button" className="button plain" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="button" onClick={() => void submit()} disabled={submitting || !file}>
            {submitting ? "Uploading…" : "Upload PDF"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ReassignModal({
  user,
  locations,
  onClose,
  onDone,
}: {
  user: UserListEntry;
  locations: Location[];
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const current = user.locationAssignments[0];
  const [locationId, setLocationId] = useState(
    locations.find((l) => l.id !== current?.location.id)?.id ?? locations[0]?.id ?? "",
  );
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await api.assignUserLocation(user.id, {
        locationId,
        notes: notes.trim() || undefined,
      });
      await onDone();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not reassign");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title={`Reassign · ${user.fullName}`} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        {error ? <div className="alert error">{error}</div> : null}
        <div>
          <strong>Current shop:</strong>{" "}
          {current ? (
            <>
              {current.location.name}{" "}
              <span className="muted">({current.location.code})</span>
            </>
          ) : (
            <span className="muted">none</span>
          )}
        </div>
        <div className="field">
          <label htmlFor="reassign-location">New shop *</label>
          <select
            id="reassign-location"
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
          >
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name} ({loc.code})
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="reassign-notes">Notes (optional)</label>
          <input
            id="reassign-notes"
            type="text"
            placeholder="e.g. covering vacation for 1 day"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={500}
          />
          <small className="hint">
            Stays in assignment history so you can see when and why each move happened.
          </small>
        </div>
        <div className="button-row">
          <button type="button" className="button plain" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="button"
            onClick={() => void submit()}
            disabled={submitting || !locationId}
          >
            {submitting ? "Saving…" : "Reassign"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Shop PIN modal (view / re-issue; nobody picks digits) ─────────────────────────────────────────────────────────────

function ShopPinModal({
  user,
  onClose,
  onDone,
}: {
  user: UserListEntry;
  onClose: () => void;
  onDone: () => void;
}) {
  // Staff can no longer choose digits (owner decision 2026-08-04): the server
  // issues a unique 8-digit PIN. This modal only VIEWS the current one or
  // requests a fresh one — there is no input to type into.
  const [pin, setPin] = useState<string | null>(null);
  const [loadingView, setLoadingView] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justGenerated, setJustGenerated] = useState(false);

  useEffect(() => {
    let stale = false;
    void shopModeApi
      .viewUserPin(user.id)
      .then((r) => {
        if (!stale) setPin(r.pin);
      })
      .catch((err: unknown) => {
        if (!stale) {
          setError(err instanceof Error ? err.message : "Could not load the PIN.");
        }
      })
      .finally(() => {
        if (!stale) setLoadingView(false);
      });
    return () => {
      stale = true;
    };
  }, [user.id]);

  const generate = async () => {
    setError(null);
    setWorking(true);
    try {
      const r = await shopModeApi.generateUserPin(user.id);
      setPin(r.pin);
      setJustGenerated(true);
      onDone(); // refresh the list's "PIN configured" badge
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not create a PIN.");
    } finally {
      setWorking(false);
    }
  };

  return (
    <Modal title={`Shop floor PIN · ${user.fullName}`} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        <p className="muted" style={{ margin: 0, fontSize: "0.9rem" }}>
          The system creates a unique 8-digit PIN — it cannot be chosen. Read it
          out to <strong>{user.fullName}</strong>; they use it to clock in on the
          shop floor tablet.
        </p>

        {error ? <div className="alert error">{error}</div> : null}

        {loadingView ? (
          <p className="muted">Loading…</p>
        ) : (
          <>
            {pin ? (
              <div className="field">
                <label>{justGenerated ? "New PIN" : "Current PIN"}</label>
                <div className="pin-reveal">{pin}</div>
              </div>
            ) : (
              <p className="muted">
                No viewable PIN yet. Create one to get started.
              </p>
            )}

            <div className="button-row">
              <button type="button" className="button plain" onClick={onClose} disabled={working}>
                Close
              </button>
              <button
                type="button"
                className="button"
                onClick={() => void generate()}
                disabled={working}
              >
                {working ? "Creating…" : pin ? "Create a new PIN" : "Create PIN"}
              </button>
            </div>

            <FobSection user={user} />
          </>
        )}
      </div>
    </Modal>
  );
}

/**
 * G-48 / OD-36 — issue and revoke a staff fob, from the tablet.
 *
 * ABSENT on a desktop browser, by design: writing a fob needs the tablet's NFC
 * reader, so on any other device this renders one honest line instead of a
 * button that cannot work. The PIN above keeps working everywhere and remains
 * the fallback for a lost fob.
 *
 * The secret exists in this component only between minting it and writing it to
 * the tag. It is never stored, never logged, and cannot be fetched again — a
 * lost fob is re-issued, which is cheap by design (OD-21).
 */
/**
 * How long the reader stays ours AFTER a fob is written (KI-144).
 *
 * Owner report 2026-10-14: a fob held on the glass for more than a second
 * after "Fob written" opened Samsung's own tag reader. The write released
 * reader mode the instant it returned, and dropping reader mode while a tag
 * is still in the field hands that tag to Android's ordinary dispatch — the
 * same mechanism the kiosk page avoids by holding the reader for its whole
 * life. Nobody lifts a fob in under a second, so the hold lingers.
 */
const FOB_LINGER_MS = 5_000;

function FobSection({ user }: { user: UserListEntry }) {
  const [fobs, setFobs] = useState<FobSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  /** The green line: a fob written or revoked. Apart from `status`, which is progress. */
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [readerReady, setReaderReady] = useState<boolean | null>(null);
  const lingerTimer = useRef<number | null>(null);

  /** Let the reader go a few seconds from now, unless another write takes it first. */
  const releaseLater = useCallback(() => {
    if (lingerTimer.current !== null) window.clearTimeout(lingerTimer.current);
    lingerTimer.current = window.setTimeout(() => {
      lingerTimer.current = null;
      void releaseFobReader();
    }, FOB_LINGER_MS);
  }, []);

  // Leaving the page releases at once; the kiosk takes its own hold on arrival.
  useEffect(
    () => () => {
      if (lingerTimer.current !== null) window.clearTimeout(lingerTimer.current);
      void releaseFobReader();
    },
    [],
  );

  const refresh = useCallback(async () => {
    try {
      setFobs(await shopModeApi.listUserFobs(user.id));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not load fobs.");
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    void refresh();
    void nfcStatus().then((s) => setReaderReady(s.supported && s.enabled));
  }, [refresh]);

  const live = fobs.find((f) => f.revokedAt === null) ?? null;

  /**
   * Mint, then write. If the WRITE fails the credential exists in the database
   * but on no tag, so the old fob is already dead and the new one was never
   * born — say exactly that, because the recovery ("issue another") is only
   * obvious once you know it.
   */
  const issue = async () => {
    setError(null);
    setDone(null);
    setBusy(true);
    if (lingerTimer.current !== null) {
      window.clearTimeout(lingerTimer.current);
      lingerTimer.current = null;
    }
    /*
     * DO NOT SAY "hold the fob" UNTIL THE READER IS ABOUT TO LISTEN.
     *
     * This used to prompt FIRST and mint the credential afterwards, so the
     * instruction appeared a whole network round trip before `writeFob` armed
     * reader mode. Anyone who did as they were told tapped into that gap —
     * Android then falls back to intent dispatch, Samsung's own tag handler
     * takes the tag, and a blank one comes back as "Empty tag". Our app never
     * saw it, which is why no error of ours ever appeared. Owner report,
     * 2026-09-14: enrolment simply never worked.
     */
    setStatus("Preparing the fob…");
    /*
     * ARM THE READER BEFORE MINTING, which is what finally closes the
     * enrolment race. `writeFob` needs the payload up front, so it could only
     * ever arm AFTER the server round trip — and a fob already resting on the
     * tablet during that gap went to Samsung's tag reader instead of to us,
     * with a freshly minted credential already burned server-side. Holding
     * first means the tag is ours from the moment the button is pressed.
     */
    await holdFobReader();
    try {
      const issued = await shopModeApi.issueUserFob(user.id);
      setStatus(`Hold the new fob against ${fobAntennaHint()}…`);
      await writeFob(issued.payload, 30_000);
      setStatus(null);
      setDone(`Fob written for ${issued.userName}. It works on the next tap.`);
      await refresh();
    } catch (err: unknown) {
      const message = nfcSupported() ? fobErrorMessage(err) : "This device cannot write fobs.";
      setError(
        `${message} Any previous fob for this person is now revoked — issue another to replace it.`,
      );
      setStatus(null);
      await refresh();
    } finally {
      // NOT released here: the fob is still on the glass. See FOB_LINGER_MS.
      releaseLater();
      setBusy(false);
    }
  };

  const revoke = async (fobId: string) => {
    setError(null);
    setDone(null);
    setBusy(true);
    try {
      await shopModeApi.revokeFob(fobId);
      setStatus(null);
      setDone("Fob revoked. It is dead on its next tap.");
      await refresh();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not revoke the fob.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fob-section">
      <h3 className="fob-section-title">Key fob</h3>

      {loading ? (
        <p className="muted">Loading…</p>
      ) : (
        <>
          {live ? (
            <p className="muted">
              Holds a fob, issued {new Date(live.issuedAt).toLocaleDateString()}
              {live.lastUsedAt
                ? ` · last used ${new Date(live.lastUsedAt).toLocaleDateString()}`
                : " · never used yet"}
            </p>
          ) : (
            <p className="muted">No fob. They clock in with the PIN above.</p>
          )}

          {error ? <div className="alert error">{error}</div> : null}
          {status ? <div className="alert">{status}</div> : null}
          {done ? <div className="alert success">{done}</div> : null}

          {readerReady === false ? (
            <p className="muted">
              Fobs are written on the shop tablet — open this page there to
              issue one.
            </p>
          ) : (
            <div className="button-row">
              {live ? (
                <button
                  type="button"
                  className="button plain"
                  onClick={() => void revoke(live.id)}
                  disabled={busy}
                >
                  Revoke fob
                </button>
              ) : null}
              <button
                type="button"
                className="button"
                onClick={() => void issue()}
                disabled={busy}
              >
                {busy ? "Waiting for fob…" : live ? "Replace fob" : "Issue fob"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
